import { ChangeDetectionStrategy, Component, computed, effect, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlotWaterfallOptions, Point, SeriesWaterfallOptions, XAxisOptions } from 'highcharts';
import { PlotOptions } from 'highcharts/highcharts.src';
import { tap, timer } from 'rxjs';
import { PbiChartTypes } from '../../../shared/chart/chart';
import { VisualFieldAllowed, VisualFieldNotAllowedReason } from '../CanAddVisualFieldError';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { FontSize } from '../VisualStyle';
import { ChartSeries, ChartVisual, ChartVisualComponent, CreateSeriesOptions, CustomLegendItem, DataTooltipPointCustomData, HighchartPointsCustomLegendTarget } from './ChartVisual.component';
import { WaterfallBuilder, WaterfallChartItemType } from './helpers/Waterfall';

@Component({
  selector: 'pbir-visual__waterfallChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WaterfallChartVisualComponent
  extends ChartVisualComponent<
    SeriesWaterfallOptions,
    WaterfallChartVisual.Options,
    WaterfallChartVisual.State,
    WaterfallChartVisual.Style> {
  get measuresRoleType() { return WaterfallChartVisual.roles.measures; }
  get groupingRoleType() { return WaterfallChartVisual.roles.category; }
  get legendRoleType() { return WaterfallChartVisual.roles.breakdown; }

  get chartType() {
    return 'waterfall';
  }


  readonly downColor = computed(() => '#B33A46');
  readonly upColor = computed(() => '#158A34');
  readonly sumColor = computed(() => '#004c78');
  readonly fallbackColor = computed(() => '#C9A600');

  readonly maxCategoryBreakdownPoints = computed(() => 5);


  computePlotOptions(): PlotOptions {
    let chartOptions: Partial<PlotWaterfallOptions> = {
      animation: !this.disableAnimations,

      allowPointSelect: true,
      dataLabels: {
        ...this.createDataLabelsOptions(),
        allowOverlap: false
      },

      showInLegend: false,
      color: this.sumColor(),
      upColor: this.upColor(),

      borderWidth: 0,
      borderRadius: 0,

      getExtremesFromAll: true,
      centerInCategory: true,

      grouping: true,
      pointWidth: undefined,
      groupPadding: 0.1,
      pointPadding: 0.1,

      nullInteraction: false,
    };

    let barSize = this.barSize();
    let barPadding = this.barPadding();
    let scrollViewSize = this.scrollViewSize();

    if (this.scrollActive()) { 
      chartOptions = {
        ...chartOptions,
        pointWidth:   barSize,
        groupPadding: 0,
      };
    }
    else {
      let space            = scrollViewSize;
      let categoryCount    = this.totalCategories();
      let spacePerCategory = space / categoryCount;

      // paddingPx = padding   * (space / categoryCount)
      // padding   = paddingPx / (space / categoryCount)
      let groupPadding = 4 /  spacePerCategory;

      chartOptions = {
        ...chartOptions,
        groupPadding
      };
    }

    return {
      ...super.computePlotOptions(),
      waterfall: chartOptions,
    };
  }

  computeNavigatorOptions(): Highcharts.NavigatorOptions {
    let options = super.computeNavigatorOptions();

    return {
      ...options,
      adaptToUpdatedData: true,
    };
  }

  computeScrollablePlotArea() {
    let _ = [this.scrollViewSize(), this.totalSize()];

    let area = {
      scrollPositionX: 0,
      scrollPositionY: 0,
      minWidth:  undefined,
      minHeight: undefined,
      opacity:   0,
      isDirty:   true,
    };

    if (this.isHorizontalPlot()) {
      area.minHeight = 1;
    }
    else {
      area.minWidth = 1;
    }

    if (this.scrollActive()) {
      let minSize = this.totalSize();

      area.opacity = 0.85;

      if (this.isHorizontalPlot()) {
        area.minHeight = minSize;
        area.minWidth  = undefined;
      }
      else {
        area.minWidth  = minSize;
        area.minHeight = undefined;
      }
    }

    return area;
  }

  readonly totalCategories = computed(() => this.categoryNames().length);
  readonly totalBars       = computed(() => this.totalCategories() * this.measureFields().length);
  readonly barPadding      = computed(() => 4);
  readonly barSize         = computed(() => FontSize.value(this.style()?.xAxis()?.fontSize, 12));
  readonly totalSize       = computed(() => Math.max(1, this.totalBars() * (this.barSize() * 2 + this.barPadding()) - this.barPadding() * 2));

  readonly scrollViewSize  = computed(() => this.chart()?.getContainerSize().w);
  readonly scrollActive    = computed(() => this.totalSize() > this.scrollViewSize());

  readonly categoryNames  = computed(() => {
    let categories: string[] = [];
    
    let waterfall = this.waterfall();
    if (!waterfall)
      return categories;

    for (let i of waterfall.items) 
      categories.push(i.displayText);

    return categories;
  });


  computeXAxis(): XAxisOptions {
    return {
      ...super.computeXAxis(),
      categories: this.categoryNames(),
    };
  }

  createAllSeries(o: CreateSeriesOptions): ChartSeries<SeriesWaterfallOptions> | ChartSeries<SeriesWaterfallOptions>[] {
    let series: ChartSeries<SeriesWaterfallOptions>[] = [];
    let data: Highcharts.PointOptionsObject[] = [];

    series.push({
      color: this.sumColor(),
      upColor: this.upColor(),
      data,
    });

    let [downColor, upColor, sumColor, fallbackColor] = [
      this.downColor(),
      this.upColor(),
      this.sumColor(),
      this.fallbackColor()
    ];
    
    let waterfall = this.waterfall();
    let measureField = this.measureFields()[0];

    let x = 0;

    for (let i = 0; i < waterfall.items.length; ++i) {
      let item = waterfall.items[i];

      if (i > 0 && item.type == WaterfallChartItemType.Total) {
        data.push({
          x: x++,
          isSum: true,
          color: sumColor,
          custom: <DataTooltipPointCustomData>{
            dataPoint: item.dataPoints[0],
            dataTooltip: item.tooltip,
            dataLabel: {
              value: item.value,
              field: measureField,
            }
          }
        });
      }
      else {
        let color;

        if (item.isOther) {
          color = fallbackColor;
        }
        else {
          color = 
            (item.type == WaterfallChartItemType.Total)    ? sumColor  :
            (item.type == WaterfallChartItemType.Increase) ? upColor   :
            (item.type == WaterfallChartItemType.Decrease) ? downColor : fallbackColor;
        }

        data.push({
          x: x++,
          y: item.value,
          color,
          custom: <DataTooltipPointCustomData>{
            dataPoints: item.dataPoints,
            dataTooltip: item.tooltip,
            dataLabel: {
              value: item.value,
              field: measureField,
              displayFormatArgs: { includePlusSign: i > 0 }
            }
          }
        });
      }
    }

    return series;
  }


  readonly waterfall = computed(() => {
    if (!this.isVisualValid())
      return null;

    let rowsResult = this.rowsResult();
    if (!rowsResult)
      return null;

    let [groupingField, legendField, measureField, formatter, dataPoints, maxCategoryBreakdownPoints] = [
      this.groupingFields()[0],
      this.legendFields()[0],
      this.measureFields()[0],
      this.formatter(rowsResult),
      this.dataPoints,
      this.maxCategoryBreakdownPoints()
    ];

    let builder = new WaterfallBuilder(
      groupingField,
      legendField,
      measureField,
      formatter,
      dataPoints);

    return builder.createWaterfall(rowsResult.rows);
  });


  private readonly _customLegendItems = signal<ReadonlyArray<CustomLegendItem>>([]);
  private _customLegendItemsUpdate = effect(() => {
    let _ = [this.isVisualValid(), this.series(), this.waterfall(), this.chart()?.chart()];
    timer(0).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        let hc = this.chart()?.chart();
        let items: CustomLegendItem[] = [];
        
        if (this.isVisualValid() && hc) {
          let series = this.series();
          let waterfall = this.waterfall();

          let increases = [];
          let decreases = [];
          let total = [];
          let other = [];

          for (let i = 0; i < waterfall.items.length; ++i) {
            let item = waterfall.items[i];
            let hcItem = hc.series[0].points[i];

            if (item.isOther) {
              other.push(hcItem);
            }
            else if (item.type == WaterfallChartItemType.Total) {
              total.push(hcItem);
            }
            else if (item.type == WaterfallChartItemType.Increase) {
              increases.push(hcItem);
            }
            else if (item.type == WaterfallChartItemType.Decrease) {
              decreases.push(hcItem);
            }
          }

          if (increases.length)
            items.push(new CustomLegendItem(new CustomLegendTarget(increases, 'Increase', this.upColor())));

          if (decreases.length)
            items.push(new CustomLegendItem(new CustomLegendTarget(decreases, 'Decrease', this.downColor())));

          if (total.length)
            items.push(new CustomLegendItem(new CustomLegendTarget(total, 'Total', this.sumColor())));

          if (other.length)
            items.push(new CustomLegendItem(new CustomLegendTarget(other, 'Other', this.fallbackColor())));
        }

        this._customLegendItems.set(items);
      })
    ).subscribe();
  });
  
  protected computeCustomLegendItems(): ReadonlyArray<CustomLegendItem> {
    return this._customLegendItems();
  }
}

class CustomLegendTarget extends HighchartPointsCustomLegendTarget {
  constructor(
    readonly points: ReadonlyArray<Point>,
    readonly name?: string,
    readonly color?: string) {
    super(points, name, color);
  }
}

export namespace WaterfallChartVisual {
  export class State extends ChartVisual.State { }
  export class Style extends ChartVisual.Style { }

  export interface Options extends ChartVisual.Options {
  }

  const PredefinedTimeValueFieldPatterns = [
    /^.*?Year$/i,
    /^.*?Quarter$/i,
    /^.*?Month$/i,
    /^.*?Month\s*Name$/i,
    /^.*?Day\s*of\s*Week$/i,
    /^.*?Day$/i,
    /^.*?Week$/i,
    /^.*?Period$/i,
    /^.*?Hour[s]?$/i,
    /^.*?Minute[s]?$/i,
    /^.*?Second[s]?$/i,
    /^.*?Date$/i,
    /^.*?Time$/i,
  ];

  export const roles = VisualFieldRoleTypeMap.create({
    category: new VisualFieldRoleType('category', {
      ...VisualFieldRoleType.Defaults.groupingCategory,
      fieldAllowed: (v, f) => {
        if (!f.usageHints?.timeValueKind) {
          if (!f.dataType.isDateOrDateTime && !f.dataType.isTimeOrDateTime) {
            // INFO: Extended datasets do not usually come with usage hints, hence we have to assume based on their name
            if (!PredefinedTimeValueFieldPatterns.some(x => x.test(f.shortTitle)))
              return VisualFieldNotAllowedReason.InvalidType;
          }
        }

        return VisualFieldAllowed;
      },
      limits: { min: 1, max: 1 },
      sortable: true,
    }), 
    breakdown: new VisualFieldRoleType('breakdown', {
      ...VisualFieldRoleType.Defaults.detailBreakdown,
      limits: { min: 0, max: 1 },
      sortable: false,
    }),
    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: { min: 1, max: 1 },
      sortable: false,
    }),
  });

  export const type = ChartVisual.defineType<WaterfallChartVisualComponent>(
    WaterfallChartVisualComponent,
    'waterfallChart',
    roles,
    {
      visualOptions: {
        legendEnabledByDefault: false,
        dataLabelsEnabledByDefault: false,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      showOverlayUntilRendered: true,
      getPbiChartType: v => PbiChartTypes.waterfallChart,
      includeSortingInQuery: false,
    });
}
